import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  MessageSquare,
  CheckSquare,
  Loader2,
  FolderKanban,
  Calendar,
  Paperclip,
  MessageCircle,
  Globe,
  Building2,
  Folder
} from 'lucide-react';
import { searchApi, SearchScope, SearchResultItem } from '@/lib/api';
import { formatRelativeTime, cn } from '@/lib/utils';

interface SearchDropdownProps {
  query: string;
  isOpen: boolean;
  onClose: () => void;
  projectId?: string;
  organizationId?: string;
}

const SCOPE_OPTIONS: { value: SearchScope; label: string; icon: typeof Globe }[] = [
  { value: 'global', label: 'All', icon: Globe },
  { value: 'organization', label: 'Organization', icon: Building2 },
  { value: 'project', label: 'Project', icon: Folder },
];

const ENTITY_CONFIG = {
  projects: { icon: FolderKanban, label: 'Projects', color: 'text-primary' },
  messages: { icon: MessageSquare, label: 'Message Threads', color: 'text-blue-500' },
  tasks: { icon: CheckSquare, label: 'Tasks', color: 'text-green-500' },
  comments: { icon: MessageCircle, label: 'Comments', color: 'text-purple-500' },
  events: { icon: Calendar, label: 'Events', color: 'text-orange-500' },
  attachments: { icon: Paperclip, label: 'Files', color: 'text-grey-500' },
};

function formatDiscussionType(slug: string): string {
  return slug.charAt(0).toUpperCase() + slug.slice(1).replace(/-/g, ' ');
}

function ResultItem({ item, onClick }: { item: SearchResultItem; onClick: () => void }) {
  const config = ENTITY_CONFIG[item.type as keyof typeof ENTITY_CONFIG];
  const Icon = config?.icon || MessageSquare;

  return (
    <button
      onClick={onClick}
      className="w-full px-4 py-3 text-left hover:bg-grey-50 transition-colors"
    >
      <div className="flex items-start gap-3">
        <div className={cn('w-8 h-8 rounded-lg flex items-center justify-center shrink-0 bg-grey-100', config?.color)}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-secondary truncate">
            {item.title}
          </p>
          {item.snippet && (
            <p className="text-xs text-grey-500 mt-0.5 line-clamp-2">
              {item.snippet}
            </p>
          )}
          <div className="flex items-center gap-2 mt-1">
            {item.projectName && (
              <span className="text-xs text-grey-400">{item.projectName}</span>
            )}
            {item.projectName && item.organizationName && (
              <span className="text-xs text-grey-300">·</span>
            )}
            <span className="text-xs text-grey-400">{item.organizationName}</span>
            <span className="text-xs text-grey-300">·</span>
            <span className="text-xs text-grey-400">{formatRelativeTime(item.createdAt)}</span>
            {item.type === 'message' && item.discussionType && (
              <span className="text-xs text-blue-500 font-medium">
                {formatDiscussionType(item.discussionType)}
              </span>
            )}
          </div>
        </div>
      </div>
    </button>
  );
}

function ResultSection({
  type,
  items,
  onItemClick
}: {
  type: keyof typeof ENTITY_CONFIG;
  items: SearchResultItem[];
  onItemClick: (item: SearchResultItem) => void;
}) {
  if (items.length === 0) return null;

  const config = ENTITY_CONFIG[type];
  const Icon = config.icon;

  return (
    <div className="border-b border-grey-100 last:border-b-0">
      <div className="px-4 py-2 bg-grey-50 border-b border-grey-100">
        <div className="flex items-center gap-2">
          <Icon className={cn('w-4 h-4', config.color)} />
          <span className="text-xs font-semibold text-grey-600 uppercase tracking-wide">
            {config.label} ({items.length})
          </span>
        </div>
      </div>
      <div className="py-1">
        {items.map((item) => (
          <ResultItem
            key={`${item.type}-${item.id}`}
            item={item}
            onClick={() => onItemClick(item)}
          />
        ))}
      </div>
    </div>
  );
}

export function SearchDropdown({ query, isOpen, onClose, projectId, organizationId }: SearchDropdownProps) {
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [scope, setScope] = useState<SearchScope>('global');

  // Adjust scope based on available context
  useEffect(() => {
    if (projectId) {
      setScope('project');
    } else if (organizationId) {
      setScope('organization');
    } else {
      setScope('global');
    }
  }, [projectId, organizationId]);

  const { data: results, isLoading } = useQuery({
    queryKey: ['search', query, scope, projectId, organizationId],
    queryFn: () => searchApi.search({
      q: query,
      scope,
      projectId: scope === 'project' ? projectId : undefined,
      organizationId: scope === 'organization' ? organizationId : undefined,
    }),
    enabled: isOpen && query.length >= 2,
  });

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose();
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, onClose]);

  const handleResultClick = (item: SearchResultItem) => {
    onClose();
    navigate(item.url);
  };

  if (!isOpen || query.length < 2) return null;

  const hasResults = results && results.totalCount > 0;

  // Determine which scope options are available
  const availableScopes = SCOPE_OPTIONS.filter(opt => {
    if (opt.value === 'project' && !projectId) return false;
    if (opt.value === 'organization' && !organizationId) return false;
    return true;
  });

  return (
    <div
      ref={dropdownRef}
      // Width AND height follow the VIEWPORT, never fixed pixels: a 450px
      // panel anchored right on a 390px phone starts at x=-76 and loses its
      // left edge off-screen (measured, 2026-08-28).
      // Height follows the VIEWPORT, not a fixed 600px: on a short window a
      // fixed panel ran past the bottom of the screen and the tail of the
      // results was simply unreachable. flex-col so the scope strip keeps its
      // height and the results list gets exactly the remainder — one scroller.
      className="absolute top-full right-0 mt-2 bg-white rounded-xl shadow-elevated border border-grey-200 max-h-[min(600px,calc(100vh-120px))] overflow-hidden z-50 w-[min(450px,calc(100vw-1.5rem))] flex flex-col"
    >
      {/* Scope selector */}
      {availableScopes.length > 1 && (
        <div className="px-4 py-2 border-b border-grey-100 flex items-center gap-2">
          <span className="text-xs text-grey-500">Search in:</span>
          <div className="flex gap-1">
            {availableScopes.map(opt => {
              const Icon = opt.icon;
              return (
                <button
                  key={opt.value}
                  onClick={() => setScope(opt.value)}
                  className={cn(
                    'px-2 py-1 text-xs rounded-md flex items-center gap-1 transition-colors',
                    scope === opt.value
                      ? 'bg-primary text-white'
                      : 'bg-grey-100 text-grey-600 hover:bg-grey-200'
                  )}
                >
                  <Icon className="w-3 h-3" />
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-5 h-5 text-grey-400 animate-spin" />
        </div>
      ) : !hasResults ? (
        <div className="px-4 py-8 text-center">
          <p className="text-sm text-grey-500">No results found</p>
          <p className="text-xs text-grey-400 mt-1">Try a different search term</p>
        </div>
      ) : (
        <div className="min-h-0 flex-1 overflow-y-auto">
          <ResultSection type="projects" items={results.results.projects} onItemClick={handleResultClick} />
          <ResultSection type="messages" items={results.results.messages} onItemClick={handleResultClick} />
          <ResultSection type="tasks" items={results.results.tasks} onItemClick={handleResultClick} />
          <ResultSection type="comments" items={results.results.comments} onItemClick={handleResultClick} />
          <ResultSection type="events" items={results.results.events} onItemClick={handleResultClick} />
          <ResultSection type="attachments" items={results.results.attachments} onItemClick={handleResultClick} />

          {results.hasMore && (
            <div className="px-4 py-3 bg-grey-50 text-center border-t border-grey-100">
              <p className="text-xs text-grey-500">
                Showing top results. Refine your search for more specific results.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
